import { useState, useCallback } from 'react';
import { Link, useSearchParams, useFetcher } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Mail, AlertTriangle } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '~/components/ui/form';
import { Spinner } from '~/components/ui/spinner';

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type FormValues = z.infer<typeof schema>;

function AlertBanner({ variant, message }: { variant: 'warning' | 'error'; message: string }) {
  const styles = {
    warning: { border: 'border-warning/20', bg: 'bg-warning-dim', icon: 'text-warning' },
    error: { border: 'border-error/20', bg: 'bg-error-dim', icon: 'text-error' },
  }[variant];
  return (
    <div className={`mb-6 flex items-start gap-3 rounded-lg border ${styles.border} ${styles.bg} px-4 py-3`}>
      <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${styles.icon}`} aria-hidden="true" />
      <p className="font-body text-sm text-text-secondary">{message}</p>
    </div>
  );
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const fetcher = useFetcher();
  const [hasReset, setHasReset] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const isInvalidated = searchParams.get('reason') === 'invalidated';
  const isInvalidToken = searchParams.get('reason') === 'invalid_token';
  const isServerError = searchParams.get('reason') === 'server_error';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  // hasReset is set to false before each submit and true on "Try again".
  // Because React 18 batches both state updates, showSent goes false→true→false
  // atomically — the "Check your email" panel never flickers mid-transition.
  const showSent = fetcher.state === 'idle' && fetcher.data?.success === true && !hasReset;
  const isLoading = fetcher.state === 'submitting';

  // R2: memoized onSubmit
  const onSubmit = useCallback((values: FormValues) => {
    setHasReset(false);
    setSubmittedEmail(values.email);
    fetcher.submit(
      { email: values.email },
      { method: 'post', action: '/api/auth/request-magic-link' }
    );
  }, [fetcher]);

  const resetForm = () => {
    setHasReset(true);
    form.reset();
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center pt-16 px-4 sm:px-6">
      <div className="w-full max-w-sm">
        {isInvalidated && !showSent && (
          <AlertBanner
            variant="warning"
            message="You've been logged out because your account was accessed from another device."
          />
        )}
        {isInvalidToken && !showSent && (
          <AlertBanner
            variant="error"
            message="This link is invalid or expired. Request a new one."
          />
        )}
        {isServerError && !showSent && (
          <AlertBanner
            variant="error"
            message="Something went wrong. Please try again."
          />
        )}

        {showSent ? (
          <div className="text-center">
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-accent-dim">
              <Mail className="h-7 w-7 text-accent-cyan" />
            </div>
            <h1 className="font-heading text-h2 text-text-primary mb-3">
              Check your email
            </h1>
            <p className="font-body text-body text-text-secondary mb-2">
              We sent a link to{' '}
              <span className="text-text-primary font-medium">{submittedEmail}</span>
            </p>
            <p className="font-body text-sm text-text-muted mb-6">
              Click the link to sign in. It expires in 24 hours.
            </p>
            <div className="mb-8 rounded-lg border border-border-subtle bg-bg-elevated px-4 py-3 text-left space-y-2">
              <p className="font-body text-sm text-text-secondary">
                <span className="text-text-primary font-medium">No email?</span> Check your spam folder or whitelist{' '}
                <span className="font-mono text-xs text-text-primary">info@foyzul.com</span>.
              </p>
              <p className="font-body text-sm text-text-secondary">
                Still nothing?{' '}
                <a
                  href="https://t.me/foyzulk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-cyan hover:text-accent-hover underline underline-offset-2 transition-colors"
                >
                  Message on Telegram
                </a>{' '}
                and I'll get you access directly.
              </p>
            </div>
            <button
              onClick={resetForm}
              className="font-body text-sm text-accent-cyan hover:text-accent-hover underline underline-offset-2 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="font-heading text-h2 text-text-primary mb-2">
                Welcome back
              </h1>
              <p className="font-body text-sm text-text-secondary">
                Enter your email to receive a magic link.
              </p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-text-secondary">Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          disabled={isLoading}
                          className="bg-bg-surface border-border-subtle text-text-primary placeholder:text-text-muted focus-visible:border-accent-cyan focus-visible:ring-accent-cyan/20"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-error" />
                    </FormItem>
                  )}
                />

                {fetcher.data?.error === 'rate_limited' && (
                  <p className="font-body text-sm text-error">
                    Too many attempts. Please wait a few minutes.
                  </p>
                )}
                {fetcher.data?.error === 'server_error' && (
                  <p className="font-body text-sm text-error">
                    Something went wrong. Please try again.
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-accent-cyan text-bg-primary hover:bg-accent-hover hover:shadow-glow transition-all duration-200"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner className="text-bg-primary" />
                      Sending...
                    </span>
                  ) : (
                    'Send Magic Link'
                  )}
                </Button>
              </form>
            </Form>

            <p className="mt-6 text-center font-body text-sm text-text-secondary">
              New here?{' '}
              <Link
                to="/"
                className="text-accent-cyan hover:text-accent-hover underline underline-offset-2 transition-colors"
              >
                Learn about the course →
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
